import { useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { PaymentPlanDetailsResponse, PaymentPlanHistoryItem, PaymentPlanStatus } from '@frontend/api-payment-plans';
import { PrintDialog } from '@frontend/assets';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { ContentLoader, Heading } from '@frontend/design-system';
import { usePaymentPlanDetails } from '../../hooks/payment-plans/use-payment-plan-details';
import { usePaymentPlanPollWorkflow } from '../../hooks/payment-plans/use-payment-plan-poll-workflow';
import { usePrintDialogue } from '../../hooks/use-print-dialogue';
import { usePaymentPlanShallowStore } from '../../providers/payment-plans-provider';
import { PaymentPlanStatusChip } from '../PaymentPlanStatusChip';
import { PrintHeader } from '../PrintHeader';
import { AlertBanner } from './atoms/alert-banner';
import { ActivatePlanAlert } from './molecules/activate-plan-alert';
import { PaymentPlanHistory } from './PaymentPlanHistory';
import { PaymentPlanSummary } from './PaymentPlanSummary';
import { PersonDetails } from './PersonDetails';

export const PaymentPlanDetailsPage = ({ paymentPlanId }: { paymentPlanId: string }) => {
  const { setFilter } = usePaymentPlanShallowStore('selectedPaymentPlan', 'setFilter');
  const { t } = useTranslation('payments');

  const [planStatus, setPlanStatus] = useState<PaymentPlanStatus | undefined>();
  const { paymentPlan, planHistory } = usePaymentPlanDetails({ paymentPlanId });
  const { loading } = usePaymentPlanPollWorkflow({ paymentPlanId });
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.min });

  const { showPrint, setShowPrint, handlePrint } = usePrintDialogue();

  useEffect(() => {
    setFilter({ search: { text: paymentPlan?.person.id } });
    return () => {
      setFilter({});
    };
  }, [paymentPlanId]);

  useEffect(() => {
    setPlanStatus(paymentPlan?.status);
  }, [paymentPlan?.status]);

  return (
    <>
      {isMobile && (
        <>
          <Heading level={1}>{t('Payment Plan')}</Heading>
          <PaymentPlanStatusChip paymentPlanStatus={planStatus} />
        </>
      )}
      {paymentPlan && isMobile && (
        <div
          css={{
            margin: theme.spacing(2, 0),
          }}
        >
          <ActivatePlanAlert planDetails={paymentPlan} />
          {planStatus === PaymentPlanStatus.Failed && <AlertBanner>{paymentPlan?.statusReason}</AlertBanner>}
        </div>
      )}
      <PersonDetails person={paymentPlan?.person} paymentPlanId={paymentPlanId} locationId={paymentPlan?.locationId} />
      {paymentPlan && (
        <PaymentPlanSummary planDetails={paymentPlan} planStatus={planStatus} handlePrint={handlePrint} />
      )}
      {!!planHistory && (
        <PaymentPlanHistory
          locationId={paymentPlan?.locationId}
          planHistory={planHistory.planHistory}
          balancePaid={paymentPlan?.paymentSummary.paidAmount}
          showPrint={false}
          paymentPlanId={paymentPlanId}
        />
      )}
      <ContentLoader show={loading} />
      {showPrint && paymentPlan && planHistory && (
        <PrintFriendly
          onClose={() => setShowPrint(false)}
          planStatus={planStatus}
          paymentPlan={paymentPlan}
          planHistory={planHistory.planHistory}
          balancePaid={planHistory.balancePaid}
          paymentPlanId={paymentPlanId}
        />
      )}
    </>
  );
};

const PrintFriendly = ({
  onClose,
  balancePaid,
  paymentPlan,
  planStatus,
  planHistory,
  paymentPlanId,
}: {
  onClose: () => void;
  balancePaid: number | undefined;
  paymentPlan: PaymentPlanDetailsResponse;
  planStatus: PaymentPlanStatus | undefined;
  planHistory: PaymentPlanHistoryItem[];
  paymentPlanId: string;
}) => {
  return (
    <PrintDialog show={true} onClose={onClose} showBackgroundGraphics>
      <div
        css={css`
          margin: ${theme.spacing(2)};
        `}
      >
        <PrintHeader />
        <PersonDetails person={paymentPlan?.person} paymentPlanId={paymentPlanId} />
        {paymentPlan && <PaymentPlanSummary planDetails={paymentPlan} planStatus={planStatus} handlePrint={() => {}} />}
        {planHistory && (
          <PaymentPlanHistory
            planHistory={planHistory}
            locationId={paymentPlan?.locationId}
            balancePaid={balancePaid}
            showPrint={true}
            paymentPlanId={paymentPlan?.id}
          />
        )}
      </div>
    </PrintDialog>
  );
};
